<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import FilterRuleEditor from './FilterRuleEditor.vue'
import { copyState, emptyState, expressionCount, isGroup, newRule, statesEqual, summarize, validateState } from './model'
import type { FilterField, FilterGroup, FilterOption, FilterPreset, FilterSchema, FilterState, SearchFilterAdapter } from './types'
import { SHARED_ANCHORS } from '@/modules/onboarding/scenarios/shared/anchors'

defineOptions({ inheritAttrs: false })

const props = defineProps<{ modelValue: FilterState; adapter: SearchFilterAdapter; defaults?: FilterPreset[]; scopeLabel?: string; addPlaceholder?: string; loading?: boolean; error?: string }>()
const emit = defineEmits<{ 'update:modelValue': [FilterState] }>()
const schema = ref<FilterSchema>({ fields: [], scopes: [] })
const presets = ref<FilterPreset[]>([])
const labels = ref<Record<string, string>>({})
const labelDepth = new Map<string, number>()
const open = ref(false)
// Панель, открытая через openPanel() (онбординг), не закрывается кликом мимо неё
const pinned = ref(false)
const panelReady = ref(false)
const requestedField = ref<string | null>(null)
const ruleEditors = new Map<number, InstanceType<typeof FilterRuleEditor>>()
let configRequest: Promise<void> | null = null
const query = ref(props.modelValue.search)
const draft = ref<FilterState>(copyState(props.modelValue))
const loadingConfig = ref(false)
const configError = ref('')
const message = ref('')
const submitted = ref(false)
const selectedPreset = ref<number | null>(null)
const presetName = ref('')
const saveDialog = ref(false)
const saveAsNew = ref(true)
const busy = ref(false)
const saveError = ref('')
const deleteTarget = ref<FilterPreset | null>(null)
const fieldPicker = ref<string | null>(null)
const allPresets = computed(() => [...(props.defaults || []), ...presets.value])
const activePreset = computed(() => allPresets.value.find(p => statesEqual(p.state, props.modelValue)))
const conditionCount = computed(() => expressionCount(props.modelValue.filter))
const addOptions = computed(() => [
  ...schema.value.fields.map(f => ({ value: `field:${f.key}`, label: f.label, group: 'Поля' })),
  ...schema.value.scopes.map(s => ({ value: `scope:${s.key}`, label: s.label, group: props.scopeLabel || 'Группы' })),
])
watch(() => props.modelValue, value => {
  query.value = value.search
}, { deep: true })
watch(open, value => {
  if (!value) {
    panelReady.value = false
    pinned.value = false
    requestedField.value = null
    return
  }
  draft.value = copyState(props.modelValue)
  draft.value.search = query.value
  selectedPreset.value = activePreset.value?.id ?? null
  submitted.value = false
  message.value = ''
})
function cacheLabels(field: string, options: FilterOption[]) {
  options.forEach(o => {
    const key = `${field}:${o.value}`
    const depth = o.ancestors?.length || 0
    if (depth >= (labelDepth.get(key) || 0)) {
      labels.value[key] = o.label
      labelDepth.set(key, depth)
    }
  })
}
async function hydrateLabels() {
  for (const item of props.modelValue.filter.conditions) {
    if (isGroup(item) || !Array.isArray(item.value) || schema.value.fields.find(f => f.key === item.field)?.type !== 'reference') continue
    try {
      cacheLabels(item.field, (await props.adapter.loadChoices(item.field, '', null, item.value)).options)
    } catch { /* Opening the condition provides a retryable choices request. */ }
  }
}
watch(() => props.modelValue, hydrateLabels, { deep: true })
function loadConfig(): Promise<void> {
  if (!configRequest) {
    configRequest = fetchConfig().finally(() => {
      configRequest = null
    })
  }
  return configRequest
}
async function fetchConfig() {
  loadingConfig.value = true
  configError.value = ''
  try {
    const [definition, saved] = await Promise.all([props.adapter.loadSchema(), props.adapter.loadPresets()])
    schema.value = definition
    presets.value = saved
    await hydrateLabels()
  } catch {
    configError.value = 'Не удалось загрузить фильтры'
  } finally {
    loadingConfig.value = false
    await focusRequestedField()
  }
}
onMounted(loadConfig)
defineExpose({
  openField: async (key: string) => {
    requestedField.value = key
    open.value = true
    await nextTick()
    if (configRequest) await configRequest
    await focusRequestedField()
  },
  openPanel: () => {
    pinned.value = true
    open.value = true
  },
  closePanel: () => {
    open.value = false
  },
})
async function focusRequestedField() {
  const key = requestedField.value
  if (!key || !open.value || !panelReady.value || loadingConfig.value || configError.value) return
  if (!schema.value.fields.some(field => field.key === key)) return
  let index = draft.value.filter.conditions.findIndex(item => !isGroup(item) && item.field === key)
  if (index < 0) {
    addField(`field:${key}`)
    index = draft.value.filter.conditions.length - 1
  }
  await nextTick()
  if (!open.value || requestedField.value !== key) return
  requestedField.value = null
  await ruleEditors.get(index)?.openValue()
}
function addField(value: string | null) {
  if (!value) return
  const [kind, key] = value.split(':')
  if (kind === 'scope') draft.value.filter.conditions.push({ scope: key, match: 'all', conditions: [] })
  else {
    const field = schema.value.fields.find(f => f.key === key)
    if (field) draft.value.filter.conditions.push(newRule(field))
  }
  fieldPicker.value = null
}
function scopeFields(group: FilterGroup): FilterField[] {
  return schema.value.scopes.find(s => s.key === group.scope)?.fields || []
}
function addQuestion(group: FilterGroup, key: string | null) {
  const field = scopeFields(group).find(f => f.key === key)
  if (field) group.conditions.push(newRule(field))
}
function apply() {
  submitted.value = true
  message.value = validateState(draft.value, schema.value)
  if (message.value) return
  emit('update:modelValue', copyState(draft.value))
  query.value = draft.value.search
  open.value = false
}
function applySearch() {
  const state = copyState(props.modelValue)
  state.search = query.value.trim()
  emit('update:modelValue', state)
  open.value = false
}
function reset() {
  draft.value = emptyState()
  query.value = ''
  emit('update:modelValue', emptyState())
  open.value = false
}
function removeCondition(index: number) {
  const state = copyState(props.modelValue)
  state.filter.conditions.splice(index, 1)
  emit('update:modelValue', state)
}
function choosePreset(preset: FilterPreset) {
  draft.value = copyState(preset.state)
  selectedPreset.value = preset.id
  submitted.value = false
  message.value = ''
}
function startSave(asNew: boolean) {
  submitted.value = true
  message.value = validateState(draft.value, schema.value)
  if (message.value) return
  saveAsNew.value = asNew
  presetName.value = asNew ? '' : presets.value.find(p => p.id === selectedPreset.value)?.name || ''
  saveError.value = ''
  saveDialog.value = true
}
async function save() {
  if (!presetName.value.trim()) {
    saveError.value = 'Укажите название фильтра'
    return
  }
  busy.value = true
  saveError.value = ''
  try {
    const saved = await props.adapter.savePreset(presetName.value.trim(), copyState(draft.value), saveAsNew.value ? undefined : selectedPreset.value!)
    presets.value = [...presets.value.filter(p => p.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    selectedPreset.value = saved.id
    saveDialog.value = false
    apply()
  } catch (error) {
    saveError.value = error instanceof Error ? error.message : 'Не удалось сохранить фильтр. Повторите попытку.'
  } finally {
    busy.value = false
  }
}
async function deletePreset() {
  if (!deleteTarget.value) return
  busy.value = true
  try {
    await props.adapter.deletePreset(deleteTarget.value.id)
    presets.value = presets.value.filter(p => p.id !== deleteTarget.value!.id)
    if (selectedPreset.value === deleteTarget.value.id) selectedPreset.value = null
    deleteTarget.value = null
  } catch {
    message.value = 'Не удалось удалить фильтр'
    deleteTarget.value = null
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div v-bind="$attrs" class="universal-search-filter">
    <VMenu
      v-model="open"
      :close-on-content-click="false"
      location="bottom start"
      :offset="8"
      :max-width="1100"
      :min-width="320"
      :persistent="pinned"
      :no-click-animation="pinned"
      @after-enter="panelReady = true; focusRequestedField()"
    >
      <template #activator="{ props: menuProps }">
        <VTextField
          v-model="query"
          :data-onboarding="SHARED_ANCHORS.searchFilter.field"
          placeholder="Поиск и фильтр"
          aria-label="Поиск и фильтр"
          variant="outlined"
          hide-details
          :loading="loading"
          maxlength="500"
          autocomplete="off"
          @keydown.enter.prevent="applySearch"
          @keydown.down.prevent="open = true"
        >
          <template #prepend-inner>
            <VIcon icon="ri-search-line" />
          </template>
          <template #append-inner>
            <VBtn
              v-if="query"
              icon="ri-close-line"
              size="small"
              variant="text"
              aria-label="Очистить поиск"
              @click="query = ''; applySearch()"
            />
            <VBtn
              v-bind="menuProps"
              :data-onboarding="SHARED_ANCHORS.searchFilter.toggle"
              variant="text"
              size="small"
              :aria-expanded="open"
              aria-label="Открыть фильтры"
              :color="conditionCount ? 'primary' : 'secondary'"
              prepend-icon="ri-filter-line"
            >
              {{ conditionCount || 'Фильтр' }}
            </VBtn>
          </template>
        </VTextField>
      </template>
      <VCard class="search-filter-panel">
        <div class="search-filter-heading">
          <span class="text-h6">Поиск и фильтр</span>
          <VBtn
            icon="ri-close-line"
            variant="text"
            size="small"
            aria-label="Закрыть фильтр"
            @click="open = false"
          />
        </div>
        <VProgressLinear v-if="loadingConfig" indeterminate />
        <VAlert
          v-if="configError"
          type="error"
          variant="tonal"
          class="ma-4"
        >
          {{ configError }} <VBtn variant="text" @click="loadConfig">
            Повторить
          </VBtn>
        </VAlert>
        <div v-else class="search-filter-body">
          <aside class="search-filter-presets" :data-onboarding="SHARED_ANCHORS.searchFilter.presets">
            <div class="text-caption font-weight-medium px-3 pt-2">
              Сохранённые фильтры
            </div>
            <VList density="compact" bg-color="transparent" aria-label="Сохранённые фильтры">
              <VListItem
                v-for="preset in allPresets"
                :key="preset.id"
                :active="selectedPreset === preset.id"
                color="primary"
                @click="choosePreset(preset)"
              >
                <VListItemTitle class="text-wrap">
                  {{ preset.name }}
                </VListItemTitle>
                <template v-if="preset.id > 0" #append>
                  <VBtn
                    icon="ri-delete-bin-line"
                    size="x-small"
                    variant="text"
                    :aria-label="`Удалить фильтр ${preset.name}`"
                    @click.stop="deleteTarget = preset"
                  />
                </template>
              </VListItem>
            </VList>
            <VBtn
              variant="text"
              prepend-icon="ri-add-line"
              class="mx-2 my-3"
              :disabled="loadingConfig || !!configError"
              @click="startSave(true)"
            >
              Сохранить фильтр
            </VBtn>
          </aside>
          <div class="search-filter-fields">
            <VTextField
              v-model="draft.search"
              :data-onboarding="SHARED_ANCHORS.searchFilter.words"
              label="Поиск по словам"
              active
              placeholder="Номер, описание, вид, отделение, место или принятые меры"
              maxlength="500"
              hide-details
              class="mb-8"
              @keydown.enter.prevent="apply"
            >
              <template #append-inner>
                <VTooltip text="Условия проверяются только среди доступных вам НС. Незаполненный ответ не считается ответом «Нет».">
                  <template #activator="{ props: hintProps }">
                    <VIcon
                      v-bind="hintProps"
                      icon="ri-information-line"
                      size="18"
                      tabindex="0"
                    />
                  </template>
                </VTooltip>
              </template>
            </VTextField>
            <VSelect
              v-if="draft.filter.conditions.length > 1"
              v-model="draft.filter.match"
              :items="[{ value: 'all', label: 'Все условия (И)' }, { value: 'any', label: 'Любое условие (ИЛИ)' }]"
              item-title="label"
              item-value="value"
              label="Показать НС, где выполняются"
              hide-details
              density="compact"
              class="mb-5"
            />
            <div class="search-filter-conditions">
              <template v-for="(item, index) in draft.filter.conditions" :key="index">
                <div v-if="isGroup(item)" class="search-filter-group">
                  <div class="d-flex align-center gap-2 mb-2">
                    <VIcon icon="ri-survey-line" color="primary" size="20" />
                    <span class="text-subtitle-1 flex-grow-1">{{ schema.scopes.find(s => s.key === item.scope)?.label || 'Недоступная группа' }}</span>
                    <VBtn
                      icon="ri-close-line"
                      size="small"
                      variant="text"
                      aria-label="Удалить группу из фильтра"
                      @click="draft.filter.conditions.splice(index, 1)"
                    />
                  </div>
                  <div class="text-caption text-medium-emphasis mb-4">
                    {{ schema.scopes.find(s => s.key === item.scope)?.hint }}
                  </div>
                  <VSelect
                    v-if="item.conditions.length > 1"
                    v-model="item.match"
                    :items="[{ value: 'all', label: 'Все ответы (И)' }, { value: 'any', label: 'Любой ответ (ИЛИ)' }]"
                    item-title="label"
                    item-value="value"
                    hide-details
                    density="compact"
                    class="mb-4"
                    aria-label="Объединение условий анкеты"
                  />
                  <FilterRuleEditor
                    v-for="(rule, ri) in item.conditions"
                    :key="ri"
                    v-model="item.conditions[ri]"
                    :fields="scopeFields(item)"
                    :adapter="adapter"
                    :validate="submitted"
                    class="mb-4"
                    @remove="item.conditions.splice(ri, 1)"
                    @labels="cacheLabels"
                  />
                  <div v-if="!item.conditions.length" class="text-body-2 mb-3">
                    {{ schema.scopes.find(s => s.key === item.scope)?.empty_label || 'Есть запись в этой группе' }}
                  </div>
                  <VAutocomplete
                    :key="item.conditions.length"
                    :model-value="null"
                    :items="scopeFields(item)"
                    item-title="label"
                    item-value="key"
                    placeholder="Добавить условие"
                    prepend-inner-icon="ri-add-line"
                    hide-details
                    density="compact"
                    @update:model-value="addQuestion(item, $event)"
                  />
                </div>
                <FilterRuleEditor
                  v-else
                  :ref="el => { if (el) ruleEditors.set(index, el as InstanceType<typeof FilterRuleEditor>); else ruleEditors.delete(index) }"
                  :model-value="item"
                  :fields="schema.fields"
                  :adapter="adapter"
                  :validate="submitted"
                  @update:model-value="draft.filter.conditions[index] = $event"
                  @remove="draft.filter.conditions.splice(index, 1)"
                  @labels="cacheLabels"
                />
              </template>
            </div>
            <VAutocomplete
              :key="draft.filter.conditions.length"
              v-model="fieldPicker"
              :data-onboarding="SHARED_ANCHORS.searchFilter.add"
              :items="addOptions"
              item-title="label"
              item-value="value"
              :placeholder="addPlaceholder || 'Добавить поле или группу'"
              prepend-inner-icon="ri-add-line"
              hide-details
              density="compact"
              class="mt-5"
              :disabled="loadingConfig"
              @update:model-value="addField"
            >
              <template #item="{ props: itemProps, item }">
                <VListItem v-bind="itemProps" :subtitle="item.raw.group" />
              </template>
            </VAutocomplete>
            <VAlert
              v-if="message"
              type="error"
              variant="tonal"
              class="mt-4"
            >
              {{ message }}
            </VAlert>
          </div>
        </div>
        <VDivider />
        <div class="search-filter-footer" :data-onboarding="SHARED_ANCHORS.searchFilter.footer">
          <VBtn
            v-if="selectedPreset && selectedPreset > 0"
            variant="text"
            :disabled="loadingConfig"
            @click="startSave(false)"
          >
            Сохранить изменения
          </VBtn>
          <VSpacer />
          <VBtn variant="text" color="secondary" @click="reset">
            Сбросить
          </VBtn>
          <VBtn prepend-icon="ri-search-line" :disabled="loadingConfig || !!configError" @click="apply">
            Найти
          </VBtn>
        </div>
      </VCard>
    </VMenu>
    <div v-if="(activePreset && activePreset.id !== -1) || conditionCount" class="search-filter-chips" :data-onboarding="SHARED_ANCHORS.searchFilter.chips">
      <VChip
        v-if="activePreset && activePreset.id !== -1"
        color="primary"
        variant="tonal"
        prepend-icon="ri-bookmark-line"
        :title="activePreset.name"
        closable
        @click="open = true"
        @click:close="reset"
      >
        <span class="search-filter-chip-label">{{ activePreset.name }}</span>
      </VChip>
      <span v-if="modelValue.filter.match === 'any' && modelValue.filter.conditions.length > 1" class="text-caption">Любое:</span>
      <VChip
        v-for="(item, index) in modelValue.filter.conditions"
        :key="index"
        closable
        :title="summarize(item, schema, labels)"
        @click="open = true"
        @click:close="removeCondition(index)"
      >
        <span class="search-filter-chip-label">{{ summarize(item, schema, labels) }}</span>
      </VChip>
    </div>
    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mt-2"
    >
      {{ error }}
    </VAlert>
  </div>
  <VDialog v-model="saveDialog" max-width="460" :persistent="busy">
    <VCard :title="saveAsNew ? 'Сохранить фильтр' : 'Изменить фильтр'">
      <VCardText>
        <VTextField
          v-model="presetName"
          label="Название"
          maxlength="100"
          autofocus
          :error-messages="saveError"
          @keydown.enter.prevent="save"
        />
      </VCardText>
      <VCardActions>
        <VSpacer /><VBtn :disabled="busy" @click="saveDialog = false">
          Отмена
        </VBtn><VBtn :loading="busy" @click="save">
          Сохранить
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
  <VDialog
    :model-value="!!deleteTarget"
    max-width="420"
    :persistent="busy"
    @update:model-value="!$event && (deleteTarget = null)"
  >
    <VCard title="Удалить фильтр?">
      <VCardText>{{ deleteTarget?.name }}</VCardText><VCardActions>
        <VSpacer /><VBtn :disabled="busy" @click="deleteTarget = null">
          Отмена
        </VBtn><VBtn color="error" :loading="busy" @click="deletePreset">
          Удалить
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.universal-search-filter { width: 100%; min-width: 0; }
.search-filter-panel { width: min(1100px, calc(100vw - 32px)); max-height: min(760px, calc(100dvh - 100px)); display: flex; flex-direction: column; }
.search-filter-heading, .search-filter-footer { display: flex; align-items: center; gap: 12px; padding: 12px 20px; flex: 0 0 auto; }
.search-filter-heading { justify-content: space-between; }

.search-filter-body { display: grid; grid-template-columns: 225px minmax(0, 1fr); min-height: 0; overflow-y: auto; overflow-x: hidden; }
.search-filter-presets { min-width: 0; background: rgba(var(--v-theme-on-surface), .025); border-inline-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); padding: 8px; }
.search-filter-fields { padding: 24px; min-width: 0; }
.search-filter-conditions { display: flex; flex-direction: column; gap: 20px; }
.search-filter-group { border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); border-radius: 8px; padding: 16px; }
.search-filter-chips { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-block-start: 10px; }
.search-filter-chips :deep(.v-chip) { max-inline-size: 100%; }
.search-filter-chips :deep(.v-chip__content) { min-inline-size: 0; }
.search-filter-chips :deep(.v-chip__close) { flex-shrink: 0; }
.search-filter-chip-label { max-width: min(450px, 70vw); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 850px) { .search-filter-body { grid-template-columns: minmax(0, 1fr); } .search-filter-presets { border-inline-end: 0; border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); } .search-filter-presets :deep(.v-list) { display: flex; overflow-x: auto; } .search-filter-presets :deep(.v-list-item) { min-width: 160px; max-width: 210px; flex-shrink: 0; } .search-filter-fields { padding: 16px; } .search-filter-footer { padding: 12px; flex-wrap: wrap; } }
</style>
